import { useForm } from "@refinedev/react-hook-form";
import { RichTextEditor } from "@components/organisms/RichTextEditor";
import { CreatableCombobox } from "@components/molecules/CreatableCombobox";
import { Controller } from "react-hook-form";
import { UpdateArticleInput } from "types/articles";
import { useTranslate, useNavigation, useGetIdentity } from "@refinedev/core";
import { DefaultTemplate } from "@components/templates/default";
import { FaChevronLeft, FaFloppyDisk } from "react-icons/fa6";
import { IAuthUser } from "@auth/firebase/interfaces";

export const ContentEdit = () => {
    const translate = useTranslate();
    const { goBack } = useNavigation();
    const {
        refineCore: { onFinish, formLoading, queryResult },
        register,
        handleSubmit,
        control,
        formState: { errors },
    } = useForm<any, any, UpdateArticleInput>({
        refineCoreProps: {
            resource: "articles",
            meta: {
                fields: ['id', 'title', 'content', 'author_id', 'landing_image', 'tags', 'published_at', 'created_at', 'updated_at']
            }
        }
    });

    const { data: user } = useGetIdentity<IAuthUser>();
    const articleData = queryResult?.data?.data;
    const isOwner = user?.user_id === articleData?.author_id;

    if (queryResult?.isLoading) {
        return (
            <DefaultTemplate>
                <div className="flex items-center justify-center min-h-[60vh]">
                    <span className="loading loading-spinner loading-lg text-primary"></span>
                </div>
            </DefaultTemplate>
        );
    }

    if (!isOwner && articleData) {
        return (
            <DefaultTemplate>
                <div className="alert alert-error mx-4 my-8">
                    <span>{translate("articles.errors.unauthorized", "No tienes permisos para editar este artículo.")}</span>
                </div>
            </DefaultTemplate>
        );
    }

    return (
        <DefaultTemplate
            left={
                <button className="btn btn-ghost gap-2" onClick={goBack}>
                    <FaChevronLeft />
                    <span>{translate("common.buttons.back", "Volver")}</span>
                </button>
            }
        >
            <div className="card bg-base-100 shadow-xl max-w-4xl mx-auto my-8">
                <div className="card-body">
                    <h2 className="card-title text-2xl mb-6">{translate("articles.titles.edit", "Editar Artículo")}</h2>

                    <form onSubmit={handleSubmit(onFinish)} className="flex flex-col gap-6">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-bold text-lg">{translate("articles.fields.title", "Título")}</span>
                            </label>
                            <input
                                type="text"
                                placeholder={translate("articles.placeholders.title", "Escribe el título...")}
                                className={`input input-bordered w-full ${errors.title ? "input-error" : ""}`}
                                {...register("title", { required: true })}
                            />
                            {errors.title && <span className="text-error text-sm mt-1">Este campo es requerido</span>}
                        </div>

                        <div className="flex flex-col md:flex-row gap-4">
                            <div className="form-control flex-1">
                                <label className="label">
                                    <span className="label-text font-bold">{translate("articles.fields.landing_image", "Imagen de Portada (URL)")}</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="https://images.unsplash.com/..."
                                    className="input input-bordered"
                                    {...register("landing_image")}
                                />
                            </div>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-bold">{translate("articles.fields.tags", "Etiquetas")}</span>
                            </label>
                            <Controller
                                control={control}
                                name="tags"
                                render={({ field }) => (
                                    <CreatableCombobox
                                        value={field.value || []}
                                        onChange={field.onChange}
                                    />
                                )}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-bold text-lg">{translate("articles.fields.content", "Contenido")}</span>
                            </label>
                            <Controller
                                control={control}
                                name="content"
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <RichTextEditor
                                        content={field.value || ""}
                                        onChange={field.onChange}
                                    />
                                )}
                            />
                            {errors.content && <span className="text-error text-sm mt-1">El contenido es requerido</span>}
                        </div>

                        <div className="form-control w-full">
                            <label className="label cursor-pointer justify-start gap-4">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-primary"
                                    defaultChecked={!!articleData?.published_at}
                                    {...register("published_at", {
                                        setValueAs: (v) => v ? new Date().toISOString() : null
                                    })}
                                />
                                <span className="label-text font-bold">Publicado</span>
                            </label>
                        </div>

                        <div className="card-actions justify-end mt-6">
                            <button
                                type="submit"
                                className={`btn btn-primary btn-wide ${formLoading ? 'loading' : ''}`}
                                disabled={formLoading}
                            >
                                <FaFloppyDisk className="mr-2" />
                                {translate("common.buttons.save", "Guardar Cambios")}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </DefaultTemplate>
    );
};
